import { useSnapshot } from "valtio"
import type { Key, CreatedTreeState, TreeNode } from "./types"

export type UseExpanded = {
  isExpanded: (key: Key) => boolean
  isNodeOrParentExpanded: (key?: Key) => boolean
  toggleExpanded: (node: TreeNode) => void
}

export function useExpanded<T>(ts: CreatedTreeState<T>): UseExpanded {
  const expandedState = useSnapshot(ts.expanded)
  const tree = useSnapshot(ts.tree)
  function isExpanded(key?: Key): boolean {
    if (!key) return false
    return !!expandedState[key]
  }

  function isNodeOrParentExpanded(key?: Key): boolean {
    if (!key) return false
    const node = tree[key]
    if (!node) return false
    const id = node.id
    const parentId = node.parentId
    return isExpanded(id) || isExpanded(parentId)
  }

  function toggleExpanded(node?: TreeNode) {
    if (!node) return
    const key = node.id
    if (isExpanded(key)) {
      const keysToRemove = [key].concat(node.children || [])
      keysToRemove.forEach((key) => {
        delete ts.expanded[key]
      })
    } else {
      ts.expanded[key] = true
    }
  }

  return {
    isExpanded,
    isNodeOrParentExpanded,
    toggleExpanded,
  }
}
